class KazenTheme {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.moneyFormat = window.Shopify?.currency?.active || 'EUR';
    this.bindMenu();
    this.bindGallery();
    this.bindProductForms();
    this.bindCart();
    this.bindStickyBar();
    this.refreshCart();
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', { style: 'currency', currency: this.moneyFormat }).format(cents / 100);
    } catch (_) {
      return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
    }
  }

  bindMenu() {
    const toggle = document.querySelector('[data-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;
    toggle.addEventListener('click', () => {
      const open = menu.classList.toggle('is-open');
      menu.setAttribute('aria-hidden', String(!open));
      toggle.setAttribute('aria-expanded', String(open));
    });
    menu.querySelectorAll('a').forEach(link => link.addEventListener('click', () => {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
    }));
  }

  bindGallery() {
    document.querySelectorAll('[data-gallery]').forEach(gallery => {
      gallery.querySelectorAll('[data-gallery-thumb]').forEach(button => {
        button.addEventListener('click', () => {
          const id = button.dataset.galleryThumb;
          gallery.querySelectorAll('.gallery__media').forEach(media => media.classList.toggle('is-active', media.dataset.mediaId === id));
          gallery.querySelectorAll('[data-gallery-thumb]').forEach(thumb => thumb.classList.toggle('is-active', thumb === button));
        });
      });
    });
  }

  bindProductForms() {
    document.querySelectorAll('[data-product-root]').forEach(root => {
      const form = root.querySelector('[data-product-form]');
      const variantsNode = root.parentElement?.querySelector('[data-product-json]') || root.nextElementSibling;
      let variants = [];
      try { variants = JSON.parse(variantsNode?.textContent || '[]'); } catch (_) {}
      const select = root.querySelector('[data-variant-select]');
      if (select) select.addEventListener('change', () => this.updateVariant(root, variants.find(v => String(v.id) === select.value)));
      if (!form) return;
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = form.querySelector('[data-add-to-cart]');
        const label = form.querySelector('[data-add-label]');
        const status = form.querySelector('[data-form-status]');
        button.disabled = true;
        if (label) label.textContent = 'Ajout…';
        try {
          const response = await fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: new FormData(form) });
          if (!response.ok) throw new Error((await response.json()).description || 'Impossible d’ajouter ce produit.');
          if (status) status.textContent = 'Ajouté au panier.';
          await this.refreshCart();
          this.openCart();
        } catch (error) {
          if (status) status.textContent = error.message;
        } finally {
          button.disabled = false;
          if (label) label.textContent = 'Ajouter au panier';
        }
      });
    });
  }

  updateVariant(root, variant) {
    if (!variant) return;
    const current = root.querySelector('[data-current-price]');
    const compare = root.querySelector('[data-compare-price]');
    const badge = root.querySelector('[data-save-badge]');
    const button = root.querySelector('[data-add-to-cart]');
    const label = root.querySelector('[data-add-label]');
    if (current) current.textContent = this.formatMoney(variant.price);
    if (compare && badge) {
      const discounted = variant.compare_at_price && variant.compare_at_price > variant.price;
      compare.hidden = !discounted;
      badge.hidden = !discounted;
      if (discounted) {
        compare.textContent = this.formatMoney(variant.compare_at_price);
        badge.textContent = `−${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%`;
      }
    }
    if (button) button.disabled = !variant.available;
    if (label) label.textContent = variant.available ? 'Ajouter au panier' : 'Épuisé';
    if (history.replaceState) history.replaceState({}, '', `${root.dataset.productUrl}?variant=${variant.id}`);
  }

  bindCart() {
    document.querySelectorAll('[data-cart-open]').forEach(button => button.addEventListener('click', () => this.openCart()));
    document.querySelectorAll('[data-cart-close]').forEach(button => button.addEventListener('click', () => this.closeCart()));
    document.addEventListener('keydown', event => { if (event.key === 'Escape') this.closeCart(); });
  }

  openCart() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
  }

  closeCart() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
  }

  async refreshCart() {
    try {
      const cart = await (await fetch('/cart.js', { headers: { 'Accept': 'application/json' } })).json();
      document.querySelectorAll('[data-cart-count]').forEach(node => node.textContent = cart.item_count);
      const body = document.querySelector('[data-cart-body]');
      const foot = document.querySelector('[data-cart-foot]');
      const total = document.querySelector('[data-cart-total]');
      if (!body || !foot) return;
      if (!cart.items.length) {
        body.innerHTML = '<p>Votre panier est vide.</p>';
        foot.hidden = true;
        return;
      }
      body.innerHTML = cart.items.map(item => {
        const details = Object.entries(item.properties || {})
          .filter(([key, value]) => value && !key.startsWith('_') && !(key === 'Title' && String(value) === 'Default Title'))
          .map(([key, value]) => `<span><strong>${this.escapeHtml(key)} :</strong> ${this.escapeHtml(String(value))}</span>`)
          .join('');
        return `<article class="drawer-line"><img src="${item.image || ''}" alt="${this.escapeHtml(item.product_title)}"><div class="drawer-line__info"><strong>${this.escapeHtml(item.product_title)}</strong>${details ? `<div class="drawer-line__details">${details}</div>` : ''}<span>${item.quantity} × ${this.formatMoney(item.final_price)}</span></div><button type="button" data-remove-line="${item.key}" aria-label="Supprimer">×</button></article>`;
      }).join('');
      foot.hidden = false;
      if (total) total.textContent = this.formatMoney(cart.total_price);
      body.querySelectorAll('[data-remove-line]').forEach(button => button.addEventListener('click', async () => {
        await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: button.dataset.removeLine, quantity: 0 }) });
        await this.refreshCart();
      }));
    } catch (_) {}
  }

  bindStickyBar() {
    const bar = document.querySelector('[data-sticky-buy]');
    const form = document.querySelector('[data-product-form]');
    if (!bar || !form) return;
    const observer = new IntersectionObserver(entries => bar.classList.toggle('is-visible', !entries[0].isIntersecting), { threshold: 0 });
    observer.observe(form);
    bar.querySelector('[data-scroll-to-form]')?.addEventListener('click', () => form.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  }

  escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }
}

document.addEventListener('DOMContentLoaded', () => new KazenTheme());
